import React, {
  HTMLAttributes,
  Suspense,
  lazy,
  useContext,
  useEffect,
  useState,
} from "react";

import { FiChevronLeft } from "react-icons/fi";

import darkPlaceholder from "shared/assets/images/dark_fallback.jpg";
import lightPlaceholder from "shared/assets/images/light_fallback.jpg";
import { ComponentErrorBoundary } from "shared/components/atoms/ComponentErrorBoundary";
import Link from "shared/components/custom/Link";
import ConfigurableCard from "shared/components/molecules/ConfigurableCard";
import ConfigurableUserCard from "shared/components/molecules/ConfigurableUserCard";
import LayoutContext from "shared/providers/Layout/context";
import GlobalContext from "shared/providers/context/GlobalContext";
import componentLoader from "shared/utils/componentLoader";
import useContextSettings from "shared/utils/hooks/useContextSettings";
import useWindowSize from "shared/utils/hooks/useWindowSize";

import { captureException } from "@sentry/nextjs";

import { renderNextButton, renderPrevButton } from "./components/NavButtons";
import { CarouselContainer } from "./styles";

const AliceCarouselImport = lazy(() =>
  componentLoader(() => import("../AliceCarouselImport")),
);

interface ICarouselProps extends HTMLAttributes<HTMLDivElement> {
  zIndex?: any;
  blackAndWhite?: boolean;
  context?: string;
  sectionLink?: string;
  sectionTitle?: string;
  marginBottom?: number;
  items?: any[];
  sectionDescription?: string;
  customResponsivenessSettings?: any;
  ExternalCard?: any;
  useProgressEnable?: boolean;
}

const Carousel: React.FC<ICarouselProps> = ({
  items,
  zIndex,
  context,
  blackAndWhite,
  marginBottom,
  ExternalCard,
  sectionLink,
  sectionTitle,
  sectionDescription,
  customResponsivenessSettings,
  useProgressEnable,
  ...rest
}) => {
  const { width: screenWidth } = useWindowSize();
  const { cardType, itemRoute, contextClassName } = useContextSettings(context);
  const { browserDoesNotSuportObserver } = useContext(LayoutContext);
  const { state } = useContext(GlobalContext);

  const [slides, setSlides] = useState([]);
  const [useGrid, setUseGrid] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const isDarkMode = state.theme.value === "dark";

  const defaultImagePlaceholder = isDarkMode
    ? darkPlaceholder
    : lightPlaceholder;

  const onSlideChanged = ({ item }) => setActiveIndex(item);

  const pendingSlides = [];

  const responsivenessSettings = {
    0: { items: 1 },
    768: { items: 2 },
    1024: { items: 3 },
    1366: { items: 4 },
    1600: { items: 5 },
  };

  useEffect(() => {
    try {
      if (items && cardType) {
        items.forEach((item, index) => {
          const routeCondition =
            item?.className === "Vico068\\CourseTools\\Course" &&
            item?.has_enrollment
              ? "/courses/content/"
              : itemRoute;

          pendingSlides.push(
            context === "external_card" ? (
              <ExternalCard content={item} />
            ) : context === "user" || context === "user_text" ? (
              <ComponentErrorBoundary>
                <ConfigurableUserCard
                  key={item?.id}
                  content={item}
                  cardType={cardType}
                  itemRoute={routeCondition}
                  useProgressEnable={useProgressEnable}
                />
              </ComponentErrorBoundary>
            ) : (
              <ComponentErrorBoundary>
                <ConfigurableCard
                  key={item?.id}
                  content={item}
                  context={context}
                  cardType={cardType}
                  itemRoute={routeCondition}
                  contextClassName={contextClassName}
                  nextLessonInfo={item?.next_lesson_slug}
                />
              </ComponentErrorBoundary>
            ),
          );
        });

        setSlides(pendingSlides);
      }
    } catch (error) {
      console.error("Erro (Carousel::setSlides):", error);
      captureException(error, {
        data: {
          message: "Erro (Carousel::setSlides):",
        },
      });
    }
  }, [items, cardType]);

  useEffect(() => {
    try {
      if (
        !browserDoesNotSuportObserver &&
        slides?.length > 0 &&
        items?.length === slides?.length
      ) {
        const imageObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const lazyImage: any = entry.target;
              if (lazyImage.dataset.src) lazyImage.src = lazyImage.dataset.src;

              const imageFailedToLoad = () => {
                lazyImage.src = defaultImagePlaceholder;

                lazyImage.removeEventListener("error", imageFailedToLoad);
              };

              lazyImage.addEventListener("error", imageFailedToLoad);
            }
          });
        });

        const images = document.querySelectorAll("img.lazy-image");

        images.forEach((image) => {
          imageObserver.observe(image);
        });

        return () => {
          imageObserver.disconnect();
        };
      }
    } catch (error) {
      console.error("Erro (Carousel::imageObserver):", error);
      captureException(error, {
        data: {
          message: "Erro (Carousel::imageObserver):",
        },
      });
    }
  }, [slides, useGrid, screenWidth]);

  useEffect(() => {
    try {
      if (slides?.length > 0) {
        const cases = {};

        if (customResponsivenessSettings) {
          for (
            let i = 0;
            i < Object.keys(customResponsivenessSettings).length;
            i += 1
          ) {
            const screenWidthConfig = Object.keys(customResponsivenessSettings)[
              i
            ];
            const { items } = customResponsivenessSettings[screenWidthConfig];

            cases[i] =
              screenWidth >= Number(screenWidthConfig) && slides.length < items;
          }
        } else {
          for (
            let i = 1;
            i < Object.keys(responsivenessSettings).length;
            i += 1
          ) {
            const screenWidthConfig = Object.keys(responsivenessSettings)[i];
            const { items } = responsivenessSettings[screenWidthConfig];

            cases[i] =
              screenWidth >= Number(screenWidthConfig) && slides.length < items;
          }
        }

        if (cases[1] || cases[2] || cases[3] || cases[4]) {
          setUseGrid(true);
        } else {
          setUseGrid(false);
        }
      }
    } catch (error) {
      console.error("Erro (Carousel::setUseGrid):", error);
      captureException(error, {
        data: {
          message: "Erro (Carousel::setUseGrid):",
        },
      });
    }
  }, [
    slides,
    screenWidth,
    responsivenessSettings,
    customResponsivenessSettings,
  ]);

  if (items?.length === slides?.length) {
    return (
      <CarouselContainer
        zIndex={zIndex}
        cardType={cardType}
        marginBottom={marginBottom}
        blackAndWhite={blackAndWhite}
        {...rest}
      >
        {sectionLink ? (
          <Link href={sectionLink}>
            <a className="section-header">
              <h3>{sectionTitle}</h3>
            </a>
          </Link>
        ) : (
          <div className="section-header">
            <h3>{sectionTitle}</h3>
          </div>
        )}

        {useGrid ? (
          <div className="slides-grid-container">
            <div className="fake-nav">
              <FiChevronLeft size={36} color="#fff" />
            </div>

            <div className="slides-grid">
              {slides.map((slide, index) => (
                <div key={index}>{slide}</div>
              ))}
            </div>
          </div>
        ) : (
          <Suspense fallback={<></>}>
            <AliceCarouselImport
              infinite
              items={slides}
              mouseTracking
              activeIndex={activeIndex}
              onSlideChanged={onSlideChanged}
              responsive={
                customResponsivenessSettings || responsivenessSettings
              }
              renderPrevButton={renderPrevButton}
              renderNextButton={renderNextButton}
              disableDotsControls
              controlsStrategy="responsive"
            />
          </Suspense>
        )}
      </CarouselContainer>
    );
  }
  return null;
};

export default Carousel;
